import { useState, useEffect, useCallback, useRef } from 'react';

const LAYOUT_STATE_KEY = 'ntb:copilot:layout:state';
const DEBOUNCE_DELAY = 300; // Debounce localStorage writes by 300ms

const getInitialLayoutState = () => {
  const saved = localStorage.getItem(LAYOUT_STATE_KEY);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Always force attached (detached: false) on refresh, but preserve other settings
      return {
        ...parsed,
        rightPanel: {
          ...parsed.rightPanel,
          detached: false // Always attached by default on refresh
        }
      };
    } catch (e) {
      console.error('Failed to parse layout state:', e);
    }
  }
  
  return {
    rightPanel: {
      visible: true,
      width: 300,
      detached: false,
      floatingPosition: {
        x: window.innerWidth - 350,
        y: 100,
        width: 400,
        height: 600
      }
    },
    layoutMode: window.innerWidth > 1200 ? 'desktop' : 
                window.innerWidth > 768 ? 'tablet' : 'mobile'
  };
};

export const useLayoutState = () => {
  const [layoutState, setLayoutState] = useState(getInitialLayoutState);
  const saveTimeoutRef = useRef(null);
  
  // Debounced localStorage save function
  const debouncedSave = useCallback((state) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(LAYOUT_STATE_KEY, JSON.stringify(state));
      } catch (e) {
        console.error('Failed to save layout state:', e);
      }
    }, DEBOUNCE_DELAY);
  }, []);
  
  // Update layout mode on resize (debounced)
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = window.innerWidth;
        const newMode = width > 1200 ? 'desktop' : 
                        width > 768 ? 'tablet' : 'mobile';
        
        setLayoutState(prev => {
          if (prev.layoutMode !== newMode) {
            const updated = { ...prev, layoutMode: newMode };
            debouncedSave(updated);
            return updated;
          }
          return prev;
        });
      }, 150); // Debounce window resize
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [debouncedSave]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  // Persist state changes (immediate for critical updates, debounced for frequent updates)
  const updateLayoutState = useCallback((updates, immediate = false) => {
    setLayoutState(prev => {
      const updated = {
        ...prev,
        ...updates
      };
      if (immediate) {
        try {
          localStorage.setItem(LAYOUT_STATE_KEY, JSON.stringify(updated));
        } catch (e) {
          console.error('Failed to save layout state:', e);
        }
      } else {
        debouncedSave(updated);
      }
      return updated;
    });
  }, [debouncedSave]);
  
  const setRightPanelWidth = useCallback((width) => {
    const clampedWidth = Math.max(250, Math.min(800, width));
    setLayoutState(prev => {
      const updated = {
        ...prev,
        rightPanel: {
          ...prev.rightPanel,
          width: clampedWidth
        }
      };
      // Debounce width updates (frequent during resize)
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);
  
  const setRightPanelDetached = useCallback((detached) => {
    setLayoutState(prev => {
      const updated = {
        ...prev,
        rightPanel: {
          ...prev.rightPanel,
          detached
        }
      };
      // Immediate save for detach/dock (critical state change)
      try {
        localStorage.setItem(LAYOUT_STATE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save layout state:', e);
      }
      return updated;
    });
  }, []);
  
  const setFloatingPosition = useCallback((position) => {
    setLayoutState(prev => {
      const updated = {
        ...prev,
        rightPanel: {
          ...prev.rightPanel,
          floatingPosition: {
            ...prev.rightPanel.floatingPosition,
            ...position
          }
        }
      };
      // Debounce position updates (frequent during drag/resize)
      debouncedSave(updated);
      return updated;
    });
  }, [debouncedSave]);
  
  return {
    layoutState,
    setRightPanelWidth,
    setRightPanelDetached,
    setFloatingPosition,
    updateLayoutState
  };
};

